import { useAuth } from "@entities/auth";
import { BRIEF_SCRIPTING_QUERY_KEY } from "@entities/brief/lib/constants";
import { useMutation } from "@shared/lib/hooks";
import { ToastService } from "@shared/lib/services";
import { BackednResponse } from "@shared/lib/types";
import {
  ICreateScriptComment,
  IGetScriptingResponse,
  IScriptingComment,
} from "@shared/lib/types/briefCampains";
import { useQueryClient } from "@tanstack/react-query";
import { BriefConfigService } from "../../services/briefConfigService";

export function useCreateScriptComment(scriptId: string) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const updateComment = (
    payload: ICreateScriptComment & { clientCommentId?: string },
    status: string,
    data?: BackednResponse<{
      comment: IScriptingComment;
      clientCommentId?: string;
    }>
  ) => {
    queryClient.setQueryData(
      BRIEF_SCRIPTING_QUERY_KEY(scriptId),
      (oldData: BackednResponse<IGetScriptingResponse>) => {
        return {
          ...oldData,
          data: {
            ...oldData.data,
            script: {
              ...oldData.data.script,
              comments:
                status === "pending"
                  ? [
                      ...(oldData.data?.script?.comments || []),
                      {
                        comment: payload.comment,
                        commentedOn: payload.commentedOn,
                        threadId: payload.threadId,
                        quotedText: payload.quotedText,
                        timeframe: oldData.data.script.timeframe?.find(
                          (t) => t._id === payload.timeframeId
                        ),
                        createdBy: {
                          firstName: user?.displayName?.split(" ")[0] || "User",
                          lastName: user?.displayName?.split(" ")[1] || "",
                          profilePicUrl: user?.photoURL || "",
                          emailId: user?.email || "",
                          _id: user?.uid || "",
                        },
                        status: status,
                        clientCommentId: payload.clientCommentId,
                        replies: [],
                      },
                    ]
                  : status === "error"
                    ? (oldData.data?.script?.comments || []).map((comment) => {
                        if (
                          comment.clientCommentId === payload.clientCommentId
                        ) {
                          return {
                            ...comment,
                            createdBy: {
                              firstName:
                                user?.displayName?.split(" ")[0] || "User",
                              lastName: user?.displayName?.split(" ")[1] || "",
                              profilePicUrl: user?.photoURL || "",
                              emailId: user?.email || "",
                              _id: user?.uid || "",
                            },
                            status: status,
                          };
                        }
                        return { ...comment };
                      })
                    : (oldData.data?.script?.comments || []).map((comment) => {
                        if (
                          comment.clientCommentId === payload.clientCommentId
                        ) {
                          return {
                            ...data?.data?.comment,
                            threadId:
                              payload.threadId || data?.data?.comment?.threadId,
                            quotedText:
                              payload.quotedText ||
                              data?.data?.comment?.quotedText,
                            clientCommentId:
                              payload.clientCommentId ||
                              data?.data?.comment?.clientCommentId,
                            createdBy: {
                              firstName:
                                user?.displayName?.split(" ")[0] || "User",
                              lastName: user?.displayName?.split(" ")[1] || "",
                              profilePicUrl: user?.photoURL || "",
                              emailId: user?.email || "",
                              _id: user?.uid || "",
                            },
                            status: status,
                          };
                        }
                        return { ...comment };
                      }),
            },
          },
        };
      }
    );
  };
  const {
    mutate: createComment,
    isLoading: isCreating,
    isSuccess: isCreated,
    data: createdCommentData,
  } = useMutation({
    mutationFn: async (
      payload: ICreateScriptComment & { clientCommentId?: string }
    ) => {
      const clientCommentId = payload.clientCommentId || `client-${Date.now()}`;
      const payloadWithClientId = { ...payload, clientCommentId };
      updateComment(payloadWithClientId, "pending");
      return BriefConfigService.createScriptComment(payload)
        .then((response) => {
          return { ...response, clientCommentId };
        })
        .catch(async (err) => {
          const responseBody = await err.response?.json?.();
          if (responseBody?.msg) ToastService.error(responseBody.msg);
          updateComment(payloadWithClientId, "error");
          throw err;
        });
    },
    options: {
      skipInvalidation: true,
    },
    onSuccess: (c, data, params) => {
      updateComment(params, "success", data);
    },
  });

  return { createComment, isCreating, isCreated, createdCommentData };
}
